import { useCallback, useEffect } from 'react';
import { useAgentChatStore } from '@/stores/agentChatStore';
import type { AgentConfig, ChatMessage, ChatSession } from '@/services/agentChatClient';

export interface UseAgentChatOptions {
  agentUrl: string;
  apiKey?: string | undefined;
  developerKey?: string | undefined;
  autoLoadSessions?: boolean | undefined;
}

export interface UseAgentChatReturn {
  // State
  messages: ChatMessage[];
  sessions: ChatSession[];
  currentSessionId: string | null;
  isStreaming: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  sendMessage: (content: string) => Promise<void>;
  createNewSession: () => void;
  switchSession: (sessionId: string) => Promise<void>;
  loadSessions: () => Promise<void>;
  cancelStream: () => void;
  clearError: () => void;
}

/**
 * Hook for managing agent chat interactions
 *
 * Provides a simple interface for:
 * - Sending messages with streaming responses
 * - Managing chat sessions
 * - Loading session history
 */
export const useAgentChat = (options: UseAgentChatOptions): UseAgentChatReturn => {
  const {
    agentConfig,
    client,
    messages,
    sessions,
    currentSessionId,
    isStreaming,
    sessionsLoading,
    messagesLoading,
    sessionsError,
    initialize,
    loadSessions,
    createNewSession,
    switchSession,
    sendMessage,
    cancelStream,
    clearError,
  } = useAgentChatStore();

  // Initialize client when options change
  useEffect(() => {
    const config: AgentConfig = {
      agentUrl: options.agentUrl,
      apiKey: options.apiKey,
      developerKey: options.developerKey,
    };

    // Only reinitialize if config actually changed
    if (
      !agentConfig ||
      agentConfig.agentUrl !== config.agentUrl ||
      agentConfig.apiKey !== config.apiKey ||
      agentConfig.developerKey !== config.developerKey
    ) {
      initialize(config);
    }
  }, [options.agentUrl, options.apiKey, options.developerKey, agentConfig, initialize]);

  // Auto-load sessions if enabled
  useEffect(() => {
    if (client && options.autoLoadSessions) {
      loadSessions();
    }
  }, [client, options.autoLoadSessions, loadSessions]);

  const handleSendMessage = useCallback(
    async (content: string) => {
      if (!content.trim()) return;
      await sendMessage(content);
    },
    [sendMessage]
  );

  const handleSwitchSession = useCallback(
    async (sessionId: string) => {
      await switchSession(sessionId);
    },
    [switchSession]
  );

  const handleLoadSessions = useCallback(async () => {
    await loadSessions();
  }, [loadSessions]);

  return {
    // State
    messages,
    sessions,
    currentSessionId,
    isStreaming,
    isLoading: sessionsLoading || messagesLoading,
    error: sessionsError,

    // Actions
    sendMessage: handleSendMessage,
    createNewSession,
    switchSession: handleSwitchSession,
    loadSessions: handleLoadSessions,
    cancelStream,
    clearError,
  };
};
